import moment from 'moment';
import {
  get,
} from 'lodash';
import {
  CatalogWithExternalEntitiesDto,
  SyllabusAssignmentDto
} from '../../apis/sherpath-course-management-service/sherpath-course-management-service.dtos';
import {
  getLocalDateFromServicesUTC,
  isValidDateEditor,
} from '../../utilities/app.utilities';
import {
  SIMULATION_EDITOR_PAGE_TITLE,
  SimulationDefaultSteps,
  SimulationStep,
  isUseAssignmentIdForPerformanceIndicator,
} from './simulation.constants';
import {
  BaseAssignmentEditorProps,
  BaseAssignmentEditorState
} from '../../hocs/with-base-assignment-editor/withBaseAssignmentEditor.hoc';
import {
  getDefaultAssignmentEditorState,
  updateStepsOnNav
} from '../../hocs/with-base-assignment-editor/with-base-assignment-editor.utilities';
import { AssignmentFormError } from '../../hocs/with-base-assignment-editor/with-base-assignment-editor.models';
import { Simulation } from './simulation.models';
import {
  AssignmentDto, AssignmentGradeType,
  AssignmentType
} from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import { RecContentItemDto } from '../../apis/rec-gateway/rec-gateway.dtos';
import { PrimaryTaxonomy } from '../../apis/rec-gateway/rec-gateway.models';
import {
  getSubtitle,
  getTitle
} from '../catalog/catalog.utilities';
import {
  MAX_ASSIGNMENT_TITLE_CHAR_LENGTH
} from '../../components/assignment-editor/assignment-editor.constant';
import { MAX_SYLLABUS_ITEM_TITLE_CHAR_LENGTH } from '../course-plan/syllabus.constants';
import { PassFailOnlyAssignmentType } from '../bulk-edit-settings/bulk-edit-settings.constants';
import { BulkEditSettingsField } from '../bulk-edit-settings/bulk-edit-settings.models';
import { FEATURE_FLAG } from '../../apis/eols-features-api/eols-features-api.constants';
import { getBooleanFromGroupFeatureFlagWithFallbackToGlobal } from '../../utilities/featureFlag.utilities';
import { isGraded, isValidGradePoints } from '../../components/assignment-editor/assignment-editor.utilities';
import { SherpathLessonStep } from '../sherpath-lesson-editor/sherpath-lesson.constants';
import { EvolveProductDto } from '../../apis/sherpath-app-facade-service/sherpath-app-facade-service.dtos';

export const getModuleIdFromSimulations = (simulationId: string, simulations: Simulation[]): string => {
  const selectedSimulation = simulations.find(simulation => simulation.simulationID === simulationId);
  return selectedSimulation.moduleID;
};

export const getLessonIdFromSimulations = (simulationId: string, simulations: Simulation[]): string => {
  const selectedSimulation = simulations.find(simulation => simulation.simulationID === simulationId);
  return selectedSimulation.moduleID === selectedSimulation.lessonID ? simulationId : selectedSimulation.lessonID;
};

export const validateSimulation = (
  syllabusAssignmentDto: SyllabusAssignmentDto,
  baseState: BaseAssignmentEditorState,
  baseProps: BaseAssignmentEditorProps
// eslint-disable-next-line sonarjs/cognitive-complexity
): AssignmentFormError[] => {
  const { messages } = baseProps;
  const errors = [];

  const assignmentGoals = get(syllabusAssignmentDto, 'assignment.assignmentGoals', []);

  if (!assignmentGoals.length) {
    errors.push({
      message: messages.ASSIGNMENT_CONTENT_ID_IS_EMPTY,
      stepId: SimulationStep.STEP1
    });
  }

  if (!syllabusAssignmentDto.assignment.title?.length) {
    errors.push({
      message: messages.ASSIGNMENT_TITLE_IS_EMPTY,
      stepId: SimulationStep.STEP1
    });
  }

  const availableDate = moment(syllabusAssignmentDto.assignment.availableDate).toDate();
  if (baseState.hasAvailableDate && !isValidDateEditor(availableDate)) {
    errors.push({
      message: messages.INVALID_AVAILABILITY_DATE,
      stepId: SimulationStep.STEP1
    });
  }

  const dueDate = moment(syllabusAssignmentDto.assignment.dueDate).toDate();
  if (baseState.hasDueDate && !isValidDateEditor(dueDate)) {
    errors.push({
      message: messages.INVALID_DUE_DATE,
      stepId: SimulationStep.STEP1
    });
  }

  if (baseState.hasDueDate
    && moment(getLocalDateFromServicesUTC(syllabusAssignmentDto.assignment.dueDate))
      .isSameOrBefore(moment(getLocalDateFromServicesUTC(syllabusAssignmentDto.assignment.availableDate)))) {
    errors.push({
      message: messages.DUE_DATE_BEFORE_AVAILABLE_DATE_ERROR_MESSAGE,
      stepId: SimulationStep.STEP1
    });
  }

  if (
    baseState.assignment?.title
    && baseState.assignment.title.length > MAX_ASSIGNMENT_TITLE_CHAR_LENGTH
  ) {
    errors.push({
      message: messages.ENTRY_EXCEEDS_LENGTH_LIMIT,
      stepId: SimulationStep.STEP1,
    });
  }

  if (
    baseState.assignment
    && baseState.assignment.assignmentGradeType === AssignmentGradeType.SCORED
    && Object.values(PassFailOnlyAssignmentType).includes(baseState.assignment.assignmentType)
  ) {
    errors.push({
      message: messages.ASSIGNMENT_TYPE_DOES_NOT_SUPPORT_SCORE_GRADING,
      fields: [BulkEditSettingsField.GRADING]
    });
  }

  const {
    featureFlagsGrouped,
    courseSectionId
  } = baseProps;

  const showGradePoints = featureFlagsGrouped && courseSectionId ? getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    featureFlagsGrouped,
    FEATURE_FLAG.IS_CUSTOM_GRADE_POINTS_ENABLED,
    courseSectionId
  ) : false;

  if (
    baseState.assignment
    && showGradePoints
    && isGraded(baseState.assignment)
    && !isValidGradePoints(baseState.assignment.gradePoints)
  ) {
    errors.push({
      message: messages.INVALID_GRADE_POINTS,
      stepId: SherpathLessonStep.STEP1,
    });
  }

  return errors;
};

export const getSimulationDefaultState = (): BaseAssignmentEditorState => {
  return {
    ...getDefaultAssignmentEditorState(),
    assignmentType: AssignmentType.SIMULATIONS,
    hasDueDate: true,
    showDueDate: true,
    showGrading: true,
    showDisplayName: true,
    pageTitle: SIMULATION_EDITOR_PAGE_TITLE,
    steps: updateStepsOnNav(SimulationDefaultSteps, 0),
    isReadOnlyDisplayName: false,
  };
};

export const getSimulationAssignmentInfo = (
  catalog: CatalogWithExternalEntitiesDto,
  contentItem: RecContentItemDto,
  primaryTaxonomies: PrimaryTaxonomy[],
  evolveProducts: EvolveProductDto[]
): {
  assignmentGoals: AssignmentDto['assignmentGoals'];
  title: AssignmentDto['title'];
  subtitle: AssignmentDto['subtitle'];
  contentId: AssignmentDto['contentId'];
} => {
  const contentId = get(contentItem, 'attributes.contentId', null);
  return {
    assignmentGoals: [{
      id: null,
      goal: 100,
      vtwId: contentId,
      text: isUseAssignmentIdForPerformanceIndicator,
    }],
    title: contentItem ? getTitle(contentItem, catalog, primaryTaxonomies, MAX_SYLLABUS_ITEM_TITLE_CHAR_LENGTH) : '',
    subtitle: contentItem ? getSubtitle(contentItem, catalog, primaryTaxonomies, evolveProducts, MAX_SYLLABUS_ITEM_TITLE_CHAR_LENGTH) : '',
    contentId
  };
};
